import { ReactNode, useEffect } from 'react'

import { HOME_DEEPLINK } from 'constants/deeplinks'
import { useNavigate } from 'react-router-dom'
import { LanguagesEnum, LanguageTrackName } from 'common/enums/languages'
import { normalizeLanguage, resolveDeviceLanguage } from 'utils'
import { useAppInfo } from 'hooks/useAppInfo'

import { FlexColumn, FlexRow } from '@interco/inter-ui/components/Flex'
import { Signal, Text, Button } from '@interco/inter-toranja'
import { Spacing } from '@interco/inter-ui/components/Spacing'
import {
  TextSize,
  TextType,
  TextWeight,
} from '@interco/inter-toranja/dist/components/Atoms/Text/types'
import { interWbNavigate } from '@interco/inter-webview-bridge'
import IcHouse from '@interco/icons/toranja/assets/action-navigation/ic_house'
import { TrackData } from '@interco/track'

import { feedbackPageData } from './constants'
import { FeedbackPageFlow, FeedbackPageType, feedbackRoutes } from './types'

type FeedbackPageProps = {
  children?: ReactNode
  feedbackType: FeedbackPageType
  flow: FeedbackPageFlow
  account?: string
  hasGoBackHome?: boolean
  onBeforeGoBackHome?: () => void
  onBeforeButtonClick?: () => void
}

export const FeedbackPage = ({
  children,
  feedbackType,
  flow,
  account,
  hasGoBackHome,
  onBeforeGoBackHome,
  onBeforeButtonClick,
}: FeedbackPageProps) => {
  const { appInfo } = useAppInfo()
  const selectedLanguage = normalizeLanguage(resolveDeviceLanguage(appInfo), LanguagesEnum.PT_BR)

  const navigate = useNavigate()

  const content = flow && feedbackPageData[selectedLanguage]?.[flow]?.[feedbackType]

  const handleButtonClick = () => {
    if (!flow) return

    const { type, route } = feedbackRoutes[flow]

    onBeforeButtonClick?.()
    switch (type) {
      case 'redirect':
        navigate(account ? `${route}?account=${account}` : route)
        break
      case 'deeplink':
        interWbNavigate.openDeepLink(route)
        break
      case 'goback':
        interWbNavigate.requestGoBack()
        break
      default:
        break
    }
  }

  const handleGoBackHome = () => {
    onBeforeGoBackHome?.()
    interWbNavigate.openDeepLink(HOME_DEEPLINK)
  }

  useEffect(() => {
    const productName = 'mortgage'
    const flowPage = 'landing_page'
    const step = 'send_form_success'

    TrackData.log({
      product_name: productName,
      flow: flowPage,
      screen_name: `${productName}/${flowPage}/${step}`,
      scenario: LanguageTrackName[selectedLanguage],
    })
  }, [selectedLanguage])

  return (
    <FlexColumn height="100vh" className="px-6">
      {hasGoBackHome && (
        <FlexRow justifyContent="flex-end" className="px-6 pt-4">
          <IcHouse
            width={24}
            height={24}
            color="var(--primary500)"
            onClick={handleGoBackHome}
            style={{ cursor: 'pointer' }}
          />
        </FlexRow>
      )}

      <FlexColumn alignItems="center" justifyContent="center" height="100%">
        <Signal
          variant={FeedbackPageType.ERROR === feedbackType ? 'error' : 'success'}
          size="large"
          state="enabled"
        />

        <Spacing mb="sm" />

        <FlexColumn alignItems="center" justifyContent="center" className="text-center gap-3">
          <Text
            textType={TextType.Display}
            textSize={TextSize.Small}
            textWeight={TextWeight.Medium}
          >
            {content?.title}
          </Text>

          {content?.description && (
            <Text
              textType={TextType.Body}
              textSize={TextSize.Large}
              textWeight={TextWeight.Regular}
            >
              {content?.description}
            </Text>
          )}
        </FlexColumn>

        {children}
      </FlexColumn>

      <FlexColumn className="pb-6" gap="1rem">
        <Button
          hierarchy="primary"
          style={{ width: '100%' }}
          onClick={handleButtonClick}
          label={content?.primaryButton}
        />
      </FlexColumn>
    </FlexColumn>
  )
}
